import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, OnInit, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { Plugins, StatusBarStyle } from '@capacitor/core';
import { environment } from '@eview/core';
import { ConfigHelpers } from '@eview/core/config/config.helpers';
import { SiteConfigItem } from '@eview/core/models/config';
import { EAuthActions, Logout } from '@eview/core/store/actions/auth.actions';
import { selectConfig } from '@eview/core/store/selectors/config.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { AppBaseComponent } from '@eview/web';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar as NgxStatusBar } from '@ionic-native/status-bar/ngx';
import { Platform } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { ToastService } from './features/toast/toast.service';
import { ToastType } from './features/toast/toast.component';
import { ApiServerError, EApiErrorActions } from '@eview/core/store/actions/error.actions';
import {
  LastJobSuccess,
  EExportActions,
  CreateJobSuccess
} from '@eview/core/store/actions/export.actions';
import { ExportService } from '@eview/core/domain/export/export.service';
import { FileSaverService } from '@eview/core';
const { StatusBar } = Plugins;
import { Location } from "@angular/common";
import * as momentZone from "moment-timezone";
import { selectTenant } from '@eview/core/store/selectors/tenant.selector';
import { ETenantActions, GetTenantBaseUrl } from '@eview/core/store/actions/tenant.actions';
import { SessionExpiredService } from '@eview/core/services/session-expired.service';
import { SessionWarningService } from '@eview/core/services/session-warning.service';
import { AuthService } from '@eview/core/auth';

momentZone.tz.setDefault(environment.defaultTimeZone);

@Component({
  selector: 'eview-root',
  templateUrl: './app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent extends AppBaseComponent implements OnInit {
  tenant$: Observable<any>;
  isSessionExpired$: Observable<boolean>;
  sessionWarning$: Observable<{ show: boolean; timeLeft: number }>;
  
  constructor(
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: NgxStatusBar,
    private breakpointObserver: BreakpointObserver,
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    @Inject(ViewContainerRef) private viewContainerRef: ViewContainerRef,
    public toast: ToastService,
    private exportService: ExportService,
    private fileSaver: FileSaverService,
    private location: Location,
    private sessionExpiredService: SessionExpiredService,
    private sessionWarningService: SessionWarningService,
    private authService: AuthService
  ) {
    super();
    this.tenant$ = this.store.select(selectTenant);
    this.isSessionExpired$ = this.sessionExpiredService.sessionExpired$;
    this.sessionWarning$ = this.sessionWarningService.warning$;
    this.isHandset$ = this.breakpointObserver
      .observe(Breakpoints.Handset)
      .pipe(
        map(result => result.matches),
        shareReplay()
      );

    this.initializeApp();

    toast.setViewContainerRef(viewContainerRef);
  }

  ngOnInit() {
    this.tenant$.subscribe(tenant => {
      document.title = tenant ? tenant.deploymentName : environment.deploymentName;
    })

    // Verificar sesión al cargar la página
    this.checkSessionOnInit();

    let host = document.location.host;
    if (environment.production && host === 'pbolern.com') {
      host = 'undpjtf-ireport-madagascar.leman.un-icc.cloud';
    }
    this.store.dispatch(new GetTenantBaseUrl({ frontend_url: host }));

    this.actions$
      .pipe(ofType(ETenantActions.GetTenantSuccess)).subscribe((result: any) => {
        document.documentElement.style.setProperty("--undp-jtf-primary-color", result.payload.colors.colors.primary_color);
        document.documentElement.style.setProperty("--undp-jtf-primary-color-transparent", result.payload.colors.colors.primary_color_transparent);
        document.documentElement.style.setProperty("--undp-jtf-secondary-color", result.payload.colors.colors.secondary_color);
        document.documentElement.style.setProperty("--undp-jtf-text-color", result.payload.colors.colors.text_color);
        document.documentElement.style.setProperty("--undp-jtf-text-header-color", result.payload.colors.colors.text_header_color);
        document.documentElement.style.setProperty("--undp-jtf-text-hover-color", result.payload.colors.colors.text_hover_color);
        document.documentElement.style.setProperty("--undp-jtf-accent-color", result.payload.colors.colors.accent_color);
        document.documentElement.style.setProperty("--undp-jtf-bg-color", result.payload.colors.colors.bg_color);
        document.documentElement.style.setProperty("--undp-jtf-dark-bg-color", result.payload.colors.colors.dark_bg_color);
        document.documentElement.style.setProperty("--undp-jtf-active-card-color", result.payload.colors.colors.active_card_color);
        document.documentElement.style.setProperty("--undp-jtf-dark-light-bg-color", result.payload.colors.colors.dark_light_bg_color);
        document.documentElement.style.setProperty("--undp-jtf-text-dark-bg-color", result.payload.colors.colors.text_dark_bg_color);
        document.documentElement.style.setProperty("--undp-jtf-link-dark-bg-color", result.payload.colors.colors.link_dark_bg_color);
        document.documentElement.style.setProperty("--undp-jtf-link-dark-bg-hover-color", result.payload.colors.colors.link_dark_bg_hover_color);
        document.documentElement.style.setProperty("--undp-jtf-btn-dark-color", result.payload.colors.colors.btn_dark_color);
        document.documentElement.style.setProperty("--undp-jtf-btn-dark-hover-bg-color", result.payload.colors.colors.btn_dark_hover_bg_color);
        document.documentElement.style.setProperty("--undp-jtf-btn-text-color", result.payload.colors.colors.btn_text_color);
        document.documentElement.style.setProperty("--undp-jtf-btn-dark-hover-color", result.payload.colors.colors.btn_dark_hover_color);
        document.documentElement.style.setProperty("--undp-jtf-status-verified-color", result.payload.colors.colors.status_verified_color);
        document.documentElement.style.setProperty("--undp-jtf-status-warning-color", result.payload.colors.colors.status_warning_color);
        document.documentElement.style.setProperty("--undp-jtf-status-unverified-bg-color", result.payload.colors.colors.status_unverified_bg_color);
        document.documentElement.style.setProperty("--undp-jtf-status-unverified-color", result.payload.colors.colors.status_unverified_color);
        document.documentElement.style.setProperty("--undp-jtf-status-evaluated-color", result.payload.colors.colors.status_evaluated_color);
        document.documentElement.style.setProperty("--undp-jtf-status-responded-color", result.payload.colors.colors.status_responded_color);
        document.documentElement.style.setProperty("--undp-jtf-region-border-color", result.payload.colors.colors.region_border_color);
      });

    this.actions$
      .pipe(ofType(EAuthActions.LoginSuccess))
      .subscribe(() => {
        const url = this.router.url.split('?')[0];
        const onAuthScreen =
          url.includes('/login') ||
          url.includes('/forgot-password') ||
          url.includes('/valida-contrasena');
        if (onAuthScreen) {
          this.router.navigate(['/dashboard/map']);
        }
        this.sessionWarningService.startMonitoring();
      });

    this.actions$
      .pipe(ofType(EAuthActions.LogoutSuccess))
      .subscribe(() => {
        this.router.navigate(['/login2']);
        this.sessionWarningService.stopMonitoring();
      });

    this.actions$
      .pipe(ofType(EApiErrorActions.ApiServerError))
      .subscribe((error: ApiServerError) => {
        const err = error.payload;
        let errorMsg = '';
        if (err && err.error) {
          const errorBody = err.error;
          if (err.status === 422 || err.status === 403 || err.status === 409 || err.status === 500) {
            errorMsg =
              errorBody.errors?.[0]?.message === 'email Record already exists!'
                ? 'EMAIL_DUPLICATE'
                : err.status === 403
                  ? 'TOAST_FORBIDDEN'
                  : this.resolveApiErrorMessage(errorBody);
          } else if (err.status !== 401) {
            errorMsg = err.message;
          } else if (
            // Mis-mapped permission denials as 401 — stay logged in, show toast
            typeof errorBody?.errors?.[0]?.message === 'string' &&
            /not allowed|forbidden|capability|form action/i.test(
              errorBody.errors[0].message
            )
          ) {
            errorMsg = 'TOAST_FORBIDDEN';
          }

          if (errorMsg) {
            const userCredErrMsg =
              'Failed to validate users entity:\nThe registration cannot be completed with the provided credentials';
            if (errorMsg === userCredErrMsg) {
              this.toast.show(ToastType.Warning, 'TOAST_USER_CREDENTIAL_ERROR');
            } else {
              this.toast.show(
                ToastType.Warning,
                err.status === 0 ? 'TOAST_GENERIC_ERROR' : errorMsg
              );
            }
          }
        }
      });

    this.actions$
      .pipe(ofType<CreateJobSuccess>(EExportActions.CreateJobSuccess))
      .subscribe(() => {
        this.toast.show(ToastType.Success, 'TOAST_CREATE_JOB_SUCCESS');
      });

    this.actions$
      .pipe(
        ofType<LastJobSuccess>(EExportActions.LastJobSuccess),
        switchMap(action =>
          this.exportService
            .readAsBlob(action.payload.url)
            .pipe(map(blob => ({ url: action.payload.url, blob })))
        )
      )
      .subscribe(({ url, blob }) => {
        if (this.location.path().split("/").pop() === 'user-management') {
          this.fileSaver.save(blob, environment.export.expoerted_user_file_name);
        } else {
          this.fileSaver.save(blob, environment.export.expoerted_file_name);
        }

      });
  }

  onSessionExpiredLogin(): void {
    this.sessionExpiredService.handleLogin();
  }

  onSessionWarningContinue(): void {
    this.sessionWarningService.stayActive();
  }

  onSessionWarningLogout(): void {
    this.sessionWarningService.handleLogout();
  }

  private checkSessionOnInit(): void {
    const token = this.authService.syncTokenFromStorage();
    if (token && (this.authService.isTokenValid(token) || this.authService.hasRefreshToken(token))) {
      this.sessionWarningService.startMonitoring();
    }
  }

  /**
   * Prefer a short, user-facing message. Never toast raw JSON / validation dumps.
   */
  private resolveApiErrorMessage(errorBody: any): string {
    const entries = Array.isArray(errorBody?.errors) ? errorBody.errors : [];
    const candidates: string[] = [];
    for (const entry of entries) {
      const raw = entry?.message ?? entry?.title;
      if (Array.isArray(raw)) {
        raw.forEach(item => {
          if (item != null && String(item).trim()) {
            candidates.push(String(item).trim());
          }
        });
      } else if (raw != null && String(raw).trim()) {
        candidates.push(String(raw).trim());
      }
    }
    if (errorBody?.message) {
      candidates.push(String(errorBody.message).trim());
    }

    for (const msg of candidates) {
      const mapped = this.mapKnownApiError(msg);
      if (mapped) {
        return mapped;
      }
      if (this.isUserFacingApiError(msg)) {
        return msg;
      }
    }
    return 'TOAST_GENERIC_ERROR';
  }

  private mapKnownApiError(message: string): string | null {
    const lower = message.toLowerCase();
    if (lower.includes('vulnerabilityrowsotherinvalidchars')) {
      return 'FORM_NO_SPECIAL_CHARACTER';
    }
    if (lower.includes('vulnerabilityrowsotherrequired')) {
      return 'VULN_ERR_OTHER_REQUIRED';
    }
    if (lower.includes('vulnerabilityrowsothertoolong')) {
      return 'FORM_MAX_LENGTH_150';
    }
    if (lower.includes('vulnerabilityrows')) {
      return 'TOAST_GENERIC_ERROR';
    }
    if (lower.includes('unpublish_reason') && lower.includes('not_empty')) {
      return 'UNPUBLISH_REASON_REQUIRED';
    }
    if (lower.includes('unpublish_reason') && lower.includes('regex')) {
      return 'FORM_NO_SPECIAL_CHARACTER';
    }
    if (message === 'Failed to validate posts entity') {
      return 'TOAST_GENERIC_ERROR';
    }
    if (lower.includes('data too long for column')) {
      return 'TOAST_GENERIC_ERROR';
    }
    if (
      lower.includes('user_delete_has_posts') ||
      lower.includes('cannot be deleted due to dependencies')
    ) {
      return 'TOAST_DELETE_USER_HAS_POSTS';
    }
    return null;
  }

  private isUserFacingApiError(message: string): boolean {
    if (!message || message.length > 180) {
      return false;
    }
    // Raw post values / vulnerability JSON dumps
    if (
      message.includes('"population"') ||
      message.includes('other_text') ||
      message.includes('[{') ||
      message.includes('{\\')
    ) {
      return false;
    }
    if (message.startsWith('Failed to validate')) {
      return false;
    }
    return true;
  }

  globalHttpLoaderEnabled =
    environment.ngHttpLoaderConfig?.globalHttpLoaderEnabled ?? true;
  isHandset$: Observable<boolean>;

  initializeApp() {
    this.splashScreen.hide();
    this.platform.ready().then(() => {
      if (this.platform.is('capacitor')) {
        StatusBar.setStyle({
          style: StatusBarStyle.Dark
        });
      } else {
        this.statusBar.styleDefault();
        this.splashScreen.hide();
      }
    });
  }
}
